// Branches compare mode (docs/decisions/branch-compare.md): the selected
// branch(es) against a benchmark, from the data the page already loads (the
// /combined branch rows and the inventory units).
//
// Every figure is built from additive counts and sums per branch (a "pool"),
// so a pooled benchmark is a ratio of totals, never an average of averages.
// The only exception is the explicit "Median branch" basis: the median of the
// per-branch rates. "Chosen branches" pools the branches the user picks, the
// same way as "Other branches".

import { gapDirection } from './lotValue';
import { isUsedUnit } from './branchView';
import { sellSpeedCategory } from './sellSpeedCategory';
import { DEFAULT_SETTINGS } from './dealershipSettings';

export const COMPARE_BASES = Object.freeze(['dealership', 'others', 'median', 'chosen']);
export const DEFAULT_COMPARE = 'dealership';
/** "Median branch" needs this many branches to mean anything. */
export const MEDIAN_MIN_BRANCHES = 4;
/** A selection figure over fewer units than this is a small sample. */
export const SMALL_SAMPLE_MIN = 5;
/** Up to this many chosen branches are named in labels; more read "4 branches". */
export const CHOSEN_NAMES_MAX = 3;

/** The bases a dealership with `branchCount` branches can pick ("Chosen branches" needs another branch). */
export const availableBases = (branchCount) => COMPARE_BASES.filter((b) => (
    (b !== 'median' || branchCount >= MEDIAN_MIN_BRANCHES) && (b !== 'chosen' || branchCount >= 2)
));

/** A basis the dealership can use, else the default. */
export const normalizeBasis = (basis, branchCount) => (
    availableBases(branchCount).includes(basis) ? basis : DEFAULT_COMPARE
);

/**
 * The chosen branches that count: known names in page order (`allNames`),
 * never one of the selection (a branch is not compared with itself).
 */
export const normalizeChosen = (chosen, allNames, selectedNames) => {
    const want = new Set(chosen || []);
    const sel = new Set(selectedNames || []);
    return allNames.filter((n) => want.has(n) && !sel.has(n));
};

/** The chosen branches as a name: "Minot, Williston, Rugby" (up to 3), else "4 branches". */
export const chosenList = (chosen) => (
    chosen.length <= CHOSEN_NAMES_MAX ? chosen.join(', ') : `${chosen.length} branches`
);

/**
 * The names behind a count label ("4 branches", "chosen branches"): "The 4
 * branches: Minot, Williston, Rugby, Velva", or null while the label names them.
 */
export const chosenNamesTip = (chosen = []) => (
    chosen.length > CHOSEN_NAMES_MAX ? `The ${chosen.length} branches: ${chosen.join(', ')}` : null
);

/** "vs whole dealership" / "vs other branches (12)" / "vs median branch" / "vs Minot, Rugby" / "vs 4 branches". */
export const benchmarkLabel = (basis, otherCount, chosen = []) => {
    if (basis === 'others') return `vs other branches (${otherCount})`;
    if (basis === 'median') return 'vs median branch';
    if (basis === 'chosen') return `vs ${chosen.length ? chosenList(chosen) : 'chosen branches'}`;
    return 'vs whole dealership';
};

/**
 * The short name next to a card figure: "18% · dealership 12%", "18% · Minot 12%".
 * More chosen branches than are named read "chosen branches" (like "other
 * branches"), since a count would run into the figure ("vs 3.6 4 branches").
 */
export const benchmarkShort = (basis, chosen = []) => {
    if (basis === 'chosen') return chosen.length && chosen.length <= CHOSEN_NAMES_MAX ? chosenList(chosen) : 'chosen branches';
    return { others: 'other branches', median: 'median branch' }[basis] || 'dealership';
};

/** The benchmark as a name (legend, table rows): "Dealership", "Other branches (12)", "Median branch", "Minot, Rugby". */
export const benchmarkName = (basis, otherCount, chosen = []) => {
    if (basis === 'chosen') return chosen.length ? chosenList(chosen) : 'Chosen branches';
    return { others: `Other branches (${otherCount})`, median: 'Median branch' }[basis] || 'Dealership';
};

/** The panel title: "Branch vs dealership" / "Branches vs other branches" / "Branch vs Minot" / … */
export const compareTitle = (basis, selectedCount, chosen = []) => (
    `${selectedCount > 1 ? 'Branches' : 'Branch'} vs ${basis === 'chosen' && chosen.length ? chosenList(chosen) : benchmarkShort(basis, chosen)}`
);

/** What each "Compare with" option means, in plain words (`plural`: several branches selected). */
export const basisHint = (basis, plural = false) => {
    const self = plural ? 'these branches' : 'this one';
    if (basis === 'chosen') return 'Compares your selection with the branches you pick, added up together.';
    if (basis === 'others') return `All other branches together, without ${self}, for a fair side-by-side.`;
    if (basis === 'median') return "The middle branch when all branches are ranked on each measure; one unusual branch can't pull it.";
    return `All branches together, including ${self}. Matches the totals elsewhere in IronSight.`;
};

/** The single-branch hints, by basis. */
export const BASIS_HINTS = Object.freeze(Object.fromEntries(COMPARE_BASES.map((b) => [b, basisHint(b)])));

/** The segmented control's options for a dealership of `branchCount` branches. */
export const compareOptions = (branchCount, plural = false) => availableBases(branchCount).map((value) => ({
    value,
    label: {
        dealership: 'Whole dealership', others: 'Other branches', median: 'Median branch', chosen: 'Chosen branches',
    }[value],
    hint: basisHint(value, plural),
}));

// ── Pools ────────────────────────────────────────────────────────────────

const SPEED_KEYS = [['Fast', 'fast'], ['Normal', 'normal'], ['Very Slow', 'verySlow'], ['Slow', 'slow']];
const speedKey = (unit) => {
    const cat = sellSpeedCategory(unit);
    if (!cat) return null;
    const hit = SPEED_KEYS.find(([prefix]) => cat.startsWith(prefix));
    return hit ? hit[1] : null;
};

/** Category name as the category mix card groups it. */
export const categoryOf = (u) => u.CategoryDisplay || u.CATEGORY || 'Unknown';

const emptyPool = () => ({
    branches: 0,
    units: 0,
    aged: 0,
    daysSum: 0,
    riskHigh: 0,
    riskScored: 0,
    active: 0,
    listings: 0,
    listedDaysSum: 0,
    listedWeight: 0,
    priceSum: 0,
    priceWeight: 0,
    inv: 0,
    pastCritical: 0,
    priced: 0,
    askingTotal: 0,
    aiTotal: 0,
    alignTotal: 0,
    overpriced: 0,
    cats: {},
    verdicts: {},
    speed: { fast: 0, normal: 0, slow: 0, verySlow: 0 },
});

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const positive = (v) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);

/**
 * One branch's additive figures: counts from its /combined row (aging past
 * the last boundary, days, risk over scored units, interest, listings
 * weighted by their count) and from its inventory units (past the critical
 * days, asking vs AI over used units with both prices, the alignment split
 * on the dealership's gap scale, categories, signals, AI sell speed).
 */
export const poolBranch = (row, units, { aging = DEFAULT_SETTINGS.aging, gapThresholds } = {}) => {
    const p = emptyPool();
    const b = row || {};
    const total = b.total || 0;
    p.branches = 1;
    p.units = total;
    p.aged = b.critical || 0;
    p.daysSum = (num(b.avg_days_in_stock) || 0) * total;
    p.riskHigh = b.risk_high || 0;
    p.riskScored = (b.risk_high || 0) + (b.risk_medium || 0) + (b.risk_low || 0);
    p.active = (b.hot || 0) + (b.warm || 0);
    const listings = num(b.total_listings) || 0;
    p.listings = listings;
    if (listings > 0 && num(b.avg_days_listed) != null) {
        p.listedDaysSum = b.avg_days_listed * listings;
        p.listedWeight = listings;
    }
    if (listings > 0 && num(b.avg_price) != null) {
        p.priceSum = b.avg_price * listings;
        p.priceWeight = listings;
    }

    const oldest = aging.boundaries[2];
    (units || []).forEach((u) => {
        const days = Number(u.DaysInStock) || 0;
        p.inv += 1;
        if (days > aging.criticalDays) p.pastCritical += 1;

        const ask = positive(u.Best_Sell_Price);
        const ai = positive(u.PredictedPrice);
        if (ask != null && ai != null) {
            // The alignment counts every unit with both prices (priceAlignment);
            // the gap sums used units only (summarizeLotValue over used units).
            p.alignTotal += 1;
            const dir = gapDirection(ask / ai - 1, gapThresholds);
            if (dir === 'above' || dir === 'wellAbove') p.overpriced += 1;
            if (isUsedUnit(u)) {
                p.priced += 1;
                p.askingTotal += ask;
                p.aiTotal += ai;
            }
        }

        const cat = categoryOf(u);
        if (!p.cats[cat]) p.cats[cat] = { units: 0, aged: 0, riskHigh: 0, riskScored: 0 };
        const c = p.cats[cat];
        c.units += 1;
        if (days > oldest) c.aged += 1;
        if (u.RiskTier) {
            c.riskScored += 1;
            if (u.RiskTier === 'Red') c.riskHigh += 1;
        }

        if (u.AnomalyVerdict) p.verdicts[u.AnomalyVerdict] = (p.verdicts[u.AnomalyVerdict] || 0) + 1;
        const sk = speedKey(u);
        if (sk) p.speed[sk] += 1;
    });
    return p;
};

const addInto = (target, src) => {
    Object.entries(src).forEach(([k, v]) => {
        if (typeof v === 'number') target[k] = (target[k] || 0) + v;
        else if (v && typeof v === 'object') {
            if (!target[k]) target[k] = {};
            addInto(target[k], v);
        }
    });
    return target;
};

/** The sum of several pools. */
export const addPools = (pools) => pools.reduce((acc, p) => addInto(acc, p), emptyPool());

// ── KPIs ─────────────────────────────────────────────────────────────────

/** Each KPI's value from a pool: { value, n } (value null when n is 0). */
const ratio = (numKey, denKey, scale = 100) => (p) => {
    const n = p[denKey] || 0;
    return { value: n > 0 ? (p[numKey] / n) * scale : null, n };
};

/**
 * The panel's KPIs, labels from the dealership's settings. `kind` sets the
 * delta unit (rate: pts, days: days, gap: pts of the asking vs AI %) and
 * `better` the good direction ('lower', 'higher', 'zero' = closer to 0).
 * Listed share is left out on purpose: /combined counts listings by city
 * from the listing feed, which is not a per-unit flag of the same stock.
 */
export const compareKpis = ({ aging = DEFAULT_SETTINGS.aging, gapThresholds = DEFAULT_SETTINGS.gapThresholds } = {}) => {
    const hi = Number((100 + gapThresholds.warn * 100).toFixed(1));
    const oldest = aging.boundaries[2];
    const crit = aging.criticalDays;
    return [
        {
            id: 'aged', label: `Older than ${oldest} days`, kind: 'rate', better: 'lower', nNoun: 'units', of: ratio('aged', 'units'),
            hint: `Share of units in stock longer than ${oldest} days, your oldest aging bucket. Lower is better: older stock ties up money and usually sells for less.`,
        },
        {
            id: 'critical', label: `Older than ${crit} days`, kind: 'rate', better: 'lower', nNoun: 'units', of: ratio('pastCritical', 'inv'),
            hint: `Share of units in stock longer than ${crit} days, your "Critical from" setting. Lower is better: these units need attention now.`,
        },
        {
            id: 'avgDays', label: 'Avg days in stock', kind: 'days', better: 'lower', nNoun: 'units', of: ratio('daysSum', 'units', 1),
            hint: 'How long units have been in stock on average. Lower is better: the stock is turning faster.',
        },
        {
            id: 'risk', label: 'High sell-speed risk', kind: 'rate', better: 'lower', nNoun: 'scored', of: ratio('riskHigh', 'riskScored'),
            hint: 'Share of units the AI expects to sell slowly, out of the units it has scored. Lower is better.',
        },
        {
            id: 'active', label: 'Active buyer interest', kind: 'rate', better: 'higher', nNoun: 'units', of: ratio('active', 'units'),
            hint: 'Share of units quoted to a customer in the last 30 days (hot or warm). Higher is better: more buyers are looking at this stock.',
        },
        {
            id: 'gap',
            label: 'Asking vs AI',
            kind: 'gap',
            better: 'zero',
            nNoun: 'priced',
            of: (p) => ({ value: p.aiTotal > 0 && p.priced > 0 ? (p.askingTotal / p.aiTotal - 1) * 100 : null, n: p.priced }),
            hint: 'How far the total asking price sits from the total AI value, for used units with both prices. Close to 0 is best: well above, units may sit longer; well below, you may leave money on the table.',
        },
        {
            id: 'overpriced', label: `Overpriced (>${hi}% of AI)`, kind: 'rate', better: 'lower', nNoun: 'priced', of: ratio('overpriced', 'alignTotal'),
            hint: `Share of priced units asking more than ${hi}% of the AI value, your "Above AI estimate from" setting. Lower is better: overpriced units tend to sit longer.`,
        },
        {
            id: 'daysListed', label: 'Avg days listed', kind: 'days', better: 'lower', nNoun: 'listings', of: ratio('listedDaysSum', 'listedWeight', 1),
            hint: 'How long listings have been online on the listing site, on average. Lower is better: listed units are selling sooner.',
        },
    ];
};

/** Median of numbers (mean of the middle two when even); null when empty. */
export const median = (values) => {
    const v = values.filter((x) => typeof x === 'number' && Number.isFinite(x)).sort((a, b) => a - b);
    if (!v.length) return null;
    const mid = Math.floor(v.length / 2);
    return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
};

/**
 * The precision a delta is shown (and judged) at. The sales performance
 * kinds (branchPerformance.js): turns (×), momentum (% vs the 12-month
 * pace), months (of supply) and money ($ per unit).
 */
const DECIMALS = {
    rate: 1, gap: 1, days: 0, turns: 1, momentum: 1, months: 1, money: 0,
};

/**
 * "About the same" band: a difference smaller than this (as shown) is
 * Similar, with a neutral tone. Shares and the asking vs AI gap in
 * percentage points, day figures in days. For asking vs AI the band applies
 * to the difference in distance from zero (closer to 0 is better).
 */
export const SIMILAR_BAND = Object.freeze({ rate: 1, gap: 1, days: 3 });

const VERDICT_OF_TONE = { good: 'better', bad: 'worse' };

/** "3.0 pts higher", "15 days longer", "1 day shorter", "0.4× higher", "$120 lower", "No difference". */
const diffWords = (kind, shown, dec) => {
    if (shown === 0) return 'No difference';
    const abs = Math.abs(shown);
    const dir = shown > 0 ? 'higher' : 'lower';
    if (kind === 'days') return `${abs} ${abs === 1 ? 'day' : 'days'} ${shown > 0 ? 'longer' : 'shorter'}`;
    if (kind === 'turns') return `${abs.toFixed(dec)}× ${dir}`;
    if (kind === 'money') return `$${abs.toLocaleString('en-US')} ${dir}`;
    if (kind === 'months') return `${abs.toFixed(dec)} months ${shown > 0 ? 'more' : 'less'}`;
    return `${abs.toFixed(dec)} pts ${shown > 0 ? 'higher' : 'lower'}`;
};

/** A value rounded as it is shown (1 decimal for shares and the gap, whole days). */
const asShown = (v, dec) => Number(v.toFixed(dec));

/**
 * Delta of selection against benchmark and whether it is good: tone
 * 'good' / 'bad' / 'neutral' and verdict 'better' / 'worse' / 'similar'
 * (inside SIMILAR_BAND), or null without a value. `text` is the signed
 * delta ("+26.0 pts"), `diffText` the plain one ("26.0 pts higher").
 * Everything is taken from the two values as shown (rounded), so 19.8% vs
 * 19.3% always reads 0.5 pts and 345d vs 346d reads 1 day. One rule
 * everywhere: Similar when the shown |difference| is under the band. A KPI
 * may set its own `band`, a `relBand` (a share of the shown benchmark, for $
 * measures) and `decimals`; otherwise its kind's apply. The band used is
 * returned as `band`.
 */
export const compareDelta = (kpi, sel, bench) => {
    if (sel == null || bench == null) return { delta: null, tone: 'neutral', text: '—', verdict: null, diffText: '—' };
    const dec = kpi.decimals ?? DECIMALS[kpi.kind] ?? 1;
    const s = asShown(sel, dec);
    const b = asShown(bench, dec);
    // A relative band ($ measures): a share of the benchmark as shown.
    const band = kpi.relBand != null ? Math.abs(b) * kpi.relBand : (kpi.band ?? SIMILAR_BAND[kpi.kind] ?? 1);
    const shown = asShown(s - b, dec);
    const d = shown;
    let tone = 'neutral';
    if (kpi.better === 'zero') {
        const closer = asShown(Math.abs(s) - Math.abs(b), dec);
        if (Math.abs(closer) >= band) tone = closer < 0 ? 'good' : 'bad';
    } else if (Math.abs(shown) >= band) {
        tone = (kpi.better === 'higher' ? d > 0 : d < 0) ? 'good' : 'bad';
    }
    const unit = { days: ' d', turns: '×', months: ' mo' }[kpi.kind] ?? ' pts';
    const sign = shown > 0 ? '+' : shown < 0 ? '−' : '±';
    const absText = kpi.kind === 'money' ? `$${Math.abs(shown).toLocaleString('en-US')}` : `${Math.abs(shown).toFixed(dec)}${unit}`;
    return {
        delta: d,
        band,
        tone,
        verdict: VERDICT_OF_TONE[tone] || 'similar',
        text: `${sign}${absText}`,
        diffText: diffWords(kpi.kind, shown, dec),
    };
};

// ── Takeaway (plain-language summary above the panel) ────────────────────

/** "Dickinson" / "These 2 branches", and whether the verb is plural. */
export const selectionSubject = (names) => (
    names.length === 1 ? { subject: names[0], plural: false } : { subject: `These ${names.length} branches`, plural: true }
);

/** "Minot", "Minot and Rugby", "Minot, Williston and Rugby", "the 4 chosen branches". */
const chosenPhrase = (chosen) => {
    if (!chosen.length) return 'the chosen branches';
    if (chosen.length > CHOSEN_NAMES_MAX) return `the ${chosen.length} chosen branches`;
    if (chosen.length === 1) return chosen[0];
    return `${chosen.slice(0, -1).join(', ')} and ${chosen[chosen.length - 1]}`;
};

/**
 * The benchmark in a sentence: "the whole dealership", "the other 12 branches", "the median branch",
 * "Minot, Williston and Rugby".
 */
export const benchmarkPhrase = (basis, otherCount, chosen = []) => {
    if (basis === 'others') return otherCount === 1 ? 'the other branch' : `the other ${otherCount} branches`;
    if (basis === 'median') return 'the median branch';
    if (basis === 'chosen') return chosenPhrase(chosen);
    return 'the whole dealership';
};

/** The benchmark leading a tip: "The dealership: …", "The other branches: …", "Minot: …", "The 4 chosen branches: …". */
export const benchmarkWho = (basis, otherCount, chosen = []) => {
    if (basis === 'chosen') {
        const p = chosenPhrase(chosen);
        return p.charAt(0).toUpperCase() + p.slice(1);
    }
    return `The ${benchmarkShort(basis)}`;
};

const more = (higher, many = 'More', few = 'Fewer') => (higher ? many : few);

// One sentence opening per KPI, by the direction of the selection's figure.
const GAP_OPENINGS = {
    aged: (hi, o) => `${more(hi)} units are older than ${o.oldest ?? DEFAULT_SETTINGS.aging.boundaries[2]} days`,
    critical: (hi, o) => `${more(hi)} units are past the critical ${o.criticalDays ?? DEFAULT_SETTINGS.aging.criticalDays} days`,
    avgDays: (hi) => (hi ? 'Units stay in stock longer' : 'Units leave stock sooner'),
    risk: (hi) => `${more(hi)} units are at high sell-speed risk`,
    active: (hi) => `${more(hi, 'More', 'Less')} buyer interest`,
    overpriced: (hi) => `${more(hi)} units are priced well above the AI value`,
    daysListed: (hi) => (hi ? 'Listings stay online longer' : 'Listings stay online for less time'),
};

const gapSentence = (r, opts) => {
    const vs = r.kind === 'days'
        ? `${Math.round(r.sel)} vs ${Math.round(r.bench)} ${r.daysPhrase || 'days on average'}`
        : `${formatKpiValue(r.kind, r.sel)} vs ${formatKpiValue(r.kind, r.bench)}`;
    let opening;
    if (r.id === 'gap') {
        if (Math.abs(r.sel) < Math.abs(r.bench)) opening = 'Asking prices are closer to the AI value';
        else opening = `Asking prices are further ${r.sel < 0 ? 'below' : 'above'} the AI value`;
    } else {
        const fn = (opts.openings || {})[r.id] || GAP_OPENINGS[r.id];
        opening = fn ? fn(r.delta > 0, opts) : `${r.label} is ${r.delta > 0 ? 'higher' : 'lower'}`;
    }
    return `${opening}: ${vs}.`;
};

/**
 * How big a gap is, comparable across measures: |sel − bench| as a share of
 * the range across branches (highest − lowest single branch), so 15 days in a
 * 100-day field weigh less than 3 pts in a 4-pt field. Without a range (or a
 * zero one) the difference in units of its similar band.
 */
export const gapSize = (r) => {
    const span = r.rangeLo != null && r.rangeHi != null ? r.rangeHi - r.rangeLo : 0;
    if (span > 0) return Math.abs(r.sel - r.bench) / span;
    return Math.abs(r.delta) / (r.band || SIMILAR_BAND[r.kind] || 1);
};

/** Up to this many "Biggest gaps" sentences. */
export const TAKEAWAY_GAPS = 2;

/**
 * The panel's plain-English takeaway: `summary` counts the measures the
 * selection does worse / better / about the same on (rows with a value and
 * not a small sample), and `gaps` gives up to 2 sentences for the largest
 * differences that are not similar, sized by `gapSize` (share of the branch
 * range, else units of the similar band). opts: { subject,
 * plural, bench, oldest, criticalDays, openings } (`openings`: extra
 * sentence openings by row id, (higher, opts) → text).
 */
export const compareTakeaway = (rows, opts) => {
    const { subject, plural = false, bench } = opts;
    const usable = rows.filter((r) => r.verdict && !r.small && r.sel != null && r.bench != null);
    const count = (v) => usable.filter((r) => r.verdict === v).length;
    const worse = count('worse');
    const better = count('better');
    const similar = count('similar');
    const total = usable.length;
    const does = plural ? 'do' : 'does';
    const is = plural ? 'are' : 'is';
    const measures = `${total} ${total === 1 ? 'measure' : 'measures'}`;

    let summary;
    if (!total) {
        const who = plural ? subject.charAt(0).toLowerCase() + subject.slice(1) : subject;
        summary = `There is not enough data yet to compare ${who} with ${bench}.`;
    } else if (!worse && !better) {
        summary = `${subject} ${is} about the same as ${bench} on all ${measures}.`;
    } else {
        const clauses = [];
        const first = worse ? 'worse' : 'better';
        clauses.push(`${subject} ${does} ${first} than ${bench} on ${first === 'worse' ? worse : better} of ${measures}`);
        if (worse && better) clauses.push(`better on ${better}`);
        if (similar) clauses.push(`about the same on ${similar}`);
        summary = clauses.length > 2
            ? `${clauses.slice(0, -1).join(', ')} and ${clauses[clauses.length - 1]}.`
            : `${clauses.join(' and ')}.`;
    }

    const gaps = usable
        .filter((r) => r.verdict !== 'similar' && r.delta != null)
        .map((r) => ({ r, size: gapSize(r) }))
        .sort((a, b) => b.size - a.size)
        .slice(0, TAKEAWAY_GAPS)
        .map(({ r }) => ({ id: r.id, verdict: r.verdict, text: gapSentence(r, opts) }));
    return { summary, gaps, counts: { worse, better, similar, total } };
};

/** Months of supply above this show as "24+ mo". */
export const MOS_DISPLAY_CAP = 24;

/** A KPI value as shown: "24.5%", "+4.6%", "150d", "2.4×", "4.2 mo", "$1,234", "—". */
export const formatKpiValue = (kind, value) => {
    if (value == null) return '—';
    if (kind === 'days') return `${Math.round(value)}d`;
    if (kind === 'turns') return `${value.toFixed(1)}×`;
    // Above two years the exact figure is noise (often one slow unit).
    if (kind === 'months') return value > MOS_DISPLAY_CAP ? `${MOS_DISPLAY_CAP}+ mo` : `${value.toFixed(1)} mo`;
    if (kind === 'money') return `$${Math.round(value).toLocaleString('en-US')}`;
    if (kind === 'gap' || kind === 'momentum') {
        const t = Math.abs(value).toFixed(1);
        if (t === '0.0') return '0.0%';
        return `${value < 0 ? '−' : '+'}${t}%`;
    }
    return `${value.toFixed(1)}%`;
};

/**
 * Per-branch pools from the /combined rows and the inventory by branch.
 * Returns { byName: Map(name → pool), names }.
 */
export const poolsByBranch = (branches, invByBranch, settings) => {
    const byName = new Map();
    (branches || []).forEach((b) => {
        byName.set(b.branch_name, poolBranch(b, (invByBranch || {})[b.branch_name] || [], settings));
    });
    return { byName, names: (branches || []).map((b) => b.branch_name) };
};

/**
 * The branch names a pooled basis adds up: every branch but the selection
 * ('others'), the chosen ones but never the selection ('chosen'), else every
 * branch. Shared with branchPerformance.perfBenchmarkOf.
 */
export const benchmarkNames = (basis, names, selected, chosen = []) => {
    if (basis === 'others') return names.filter((n) => !selected.has(n));
    if (basis === 'chosen') {
        const want = new Set(chosen);
        return names.filter((n) => want.has(n) && !selected.has(n));
    }
    return [...names];
};

/**
 * The benchmark for a basis: pooled (whole dealership, other branches, chosen
 * branches) or the per-branch pools for the median. { kind: 'pool', pool } |
 * { kind: 'median', pools }. `chosen`: the picked branch names ('chosen').
 */
export const benchmarkOf = (basis, { byName, names }, selected, chosen = []) => {
    if (basis === 'median') {
        const all = names.map((n) => byName.get(n));
        return { kind: 'median', pools: all, branches: all.length };
    }
    const pooled = benchmarkNames(basis, names, selected, chosen).map((n) => byName.get(n));
    return { kind: 'pool', pool: addPools(pooled), branches: pooled.length };
};

/** A figure of the benchmark: pooled ratio, or median of the per-branch ratios (branches with n > 0). */
export const benchmarkFigure = (bench, of) => {
    if (bench.kind === 'median') {
        const vals = bench.pools.map(of).filter((r) => r.n > 0 && r.value != null);
        return { value: median(vals.map((r) => r.value)), n: vals.length, nNoun: 'branches' };
    }
    return of(bench.pool);
};

/**
 * Rows of the "Branch vs dealership" panel: one per KPI with the selection
 * and benchmark values, the delta and its tone, n and the small-sample flag.
 */
export const buildCompareRows = (selPool, bench, settings, branchPools = null) => compareKpis(settings).map((kpi) => {
    const s = kpi.of(selPool);
    const b = benchmarkFigure(bench, kpi.of);
    const delta = compareDelta(kpi, s.value, b.value);
    // Lowest and highest single branch (n > 0): the ends of the comparison track.
    const spread = (branchPools || []).map(kpi.of).filter((r) => r.n > 0 && r.value != null).map((r) => r.value);
    return {
        rangeLo: spread.length ? Math.min(...spread) : null,
        rangeHi: spread.length ? Math.max(...spread) : null,
        id: kpi.id,
        label: kpi.label,
        kind: kpi.kind,
        better: kpi.better,
        hint: kpi.hint,
        sel: s.value,
        selN: s.n,
        nNoun: kpi.nNoun,
        bench: b.value,
        benchN: b.n,
        benchNoun: b.nNoun || kpi.nNoun,
        ...delta,
        small: s.n > 0 && s.n < SMALL_SAMPLE_MIN,
    };
});

/**
 * Category breakdown (mix effect): per category of the selection, units and
 * the share past the last aging boundary and the high sell-speed risk share
 * (of scored units), selection against benchmark. Most units first.
 */
export const buildCategoryCompare = (selPool, bench) => {
    const ofCat = (cat, numKey, denKey) => (p) => {
        const c = p.cats[cat];
        const n = c ? c[denKey] : 0;
        return { value: n > 0 ? (c[numKey] / n) * 100 : null, n };
    };
    const rate = { kind: 'rate', better: 'lower' };
    return Object.entries(selPool.cats)
        .map(([name, c]) => {
            const agedB = benchmarkFigure(bench, ofCat(name, 'aged', 'units'));
            const riskB = benchmarkFigure(bench, ofCat(name, 'riskHigh', 'riskScored'));
            const agedSel = c.units > 0 ? (c.aged / c.units) * 100 : null;
            const riskSel = c.riskScored > 0 ? (c.riskHigh / c.riskScored) * 100 : null;
            return {
                name,
                units: c.units,
                agedSel,
                agedBench: agedB.value,
                agedDelta: compareDelta(rate, agedSel, agedB.value),
                riskSel,
                riskScored: c.riskScored,
                riskBench: riskB.value,
                riskDelta: compareDelta(rate, riskSel, riskB.value),
                small: c.units < SMALL_SAMPLE_MIN,
            };
        })
        .sort((a, b) => b.units - a.units || a.name.localeCompare(b.name));
};

// ── Card references (share-based) ────────────────────────────────────────

/** Benchmark share (%) of units in a category (of all inventory units). */
export const benchCategoryShare = (bench, cat) => benchmarkFigure(bench, (p) => ({
    value: p.inv > 0 ? ((p.cats[cat]?.units || 0) / p.inv) * 100 : null, n: p.inv,
})).value;

/** Benchmark share (%) of units with an anomaly verdict. */
export const benchVerdictShare = (bench, verdict) => benchmarkFigure(bench, (p) => ({
    value: p.inv > 0 ? ((p.verdicts[verdict] || 0) / p.inv) * 100 : null, n: p.inv,
})).value;

/** Benchmark share (%) of every unit in an AI sell-speed bucket (as the card's %). */
export const benchSpeedShare = (bench, key) => benchmarkFigure(bench, (p) => ({
    value: p.inv > 0 ? ((p.speed[key] || 0) / p.inv) * 100 : null, n: p.inv,
})).value;

/** Benchmark average listing price and days listed (listing-weighted). */
export const benchListing = (bench) => ({
    avgPrice: benchmarkFigure(bench, ratio('priceSum', 'priceWeight', 1)).value,
    avgDaysListed: benchmarkFigure(bench, ratio('listedDaysSum', 'listedWeight', 1)).value,
});

/**
 * The benchmark's used-lot value for "AI value by location": pooled totals
 * (whole dealership, other branches), or for the median branch only the
 * median of the branches' asking vs AI gaps (totals have no median meaning).
 * gapPct is a fraction, as in summarizeLotValue.
 */
export const benchmarkLot = (bench) => {
    if (bench.kind === 'median') {
        const gap = benchmarkFigure(bench, (p) => ({
            value: p.aiTotal > 0 && p.priced > 0 ? p.askingTotal / p.aiTotal - 1 : null, n: p.priced,
        })).value;
        return { pricedCount: null, askingTotal: null, aiTotal: null, gapTotal: null, gapPct: gap };
    }
    const p = bench.pool;
    return {
        pricedCount: p.priced,
        askingTotal: p.askingTotal,
        aiTotal: p.aiTotal,
        gapTotal: p.askingTotal - p.aiTotal,
        gapPct: p.aiTotal > 0 && p.priced > 0 ? p.askingTotal / p.aiTotal - 1 : null,
    };
};

/**
 * The labels at the ends of a comparison track. With no branch range (lo or
 * hi missing) null; when every branch has the same figure { same, text };
 * otherwise { lo, hi }, with "(this branch)" where a single selected branch
 * is itself the lowest or highest.
 */
export const rangeEnds = ({ kind, lo, hi, sel, single }) => {
    if (lo == null || hi == null) return null;
    const fmt = (v) => formatKpiValue(kind, v);
    if (fmt(lo) === fmt(hi)) return { same: true, text: `Same at every branch: ${fmt(lo)}` };
    const self = (v) => (single && sel != null && fmt(sel) === fmt(v) ? ' (this branch)' : '');
    return { same: false, lo: `Lowest branch ${fmt(lo)}${self(lo)}`, hi: `Highest ${fmt(hi)}${self(hi)}` };
};

/** Whole-number share for a card reference ("12%"), "—" when unknown. */
export const formatShare = (v) => (v == null ? '—' : `${Math.round(v)}%`);

// ── Address (Branches tab view state) ────────────────────────────────────

// Query params of the Branches tab. Not ?branch=, which is the inventory's.
export const BRANCHES_PARAM = 'branches';
export const COMPARE_PARAM = 'compare';
/** The chosen branches of the 'chosen' basis: ?compare=chosen&vs=Minot,Rugby. */
export const CHOSEN_PARAM = 'vs';
const ALL = 'all';
const NONE = 'none';

const nameList = (raw) => raw.split(',').map((s) => s.trim()).filter(Boolean);

const selectedFrom = (raw, allNames) => {
    if (raw === null) return null;
    const value = raw.trim();
    if (value.toLowerCase() === ALL) return [...allNames];
    if (value.toLowerCase() === NONE) return [];
    const wanted = new Set(nameList(value));
    const selected = allNames.filter((n) => wanted.has(n));
    return selected.length ? selected : null;
};

/**
 * The Branches tab's view from the address: { selected: string[] | null,
 * compare, chosen: string[] }. `selected` is null when the address names
 * nothing usable (the default then applies: the largest branch); unknown
 * names are ignored. `chosen` (?vs=, only with ?compare=chosen) drops
 * unknown names and the selection's own.
 */
export const parseBranchesView = (search, allNames) => {
    const q = new URLSearchParams(search || '');
    const compare = normalizeBasis(q.get(COMPARE_PARAM), allNames.length);
    const selected = selectedFrom(q.get(BRANCHES_PARAM), allNames);
    const vs = q.get(CHOSEN_PARAM);
    const chosen = compare === 'chosen' && vs ? normalizeChosen(nameList(vs), allNames, selected) : [];
    return { selected, compare, chosen };
};

/** A search string in URLSearchParams' own spelling (so "a,b" equals "a%2Cb"). */
export const canonicalSearch = (search) => {
    const s = new URLSearchParams(search || '').toString();
    return s ? `?${s}` : '';
};

/** Drops the tab's params (the other tabs do not use them). */
export const stripBranchesView = (search) => {
    const q = new URLSearchParams(search || '');
    q.delete(BRANCHES_PARAM);
    q.delete(COMPARE_PARAM);
    q.delete(CHOSEN_PARAM);
    const s = q.toString();
    return s ? `?${s}` : '';
};

/**
 * The address search for a view, other params kept. Defaults are left out
 * (the largest branch alone, compare with the whole dealership), so the
 * plain page keeps a plain address. `chosen` goes in ?vs= with the 'chosen'
 * basis only.
 */
export const serializeBranchesView = (search, {
    selectedNames, allNames, compare, chosen = [],
}) => {
    const q = new URLSearchParams(stripBranchesView(search));
    const isDefaultSel = selectedNames.length === 1 && selectedNames[0] === allNames[0];
    if (!isDefaultSel) {
        let value;
        if (selectedNames.length === 0) value = NONE;
        else if (allNames.length > 0 && selectedNames.length === allNames.length) value = ALL;
        else value = selectedNames.join(',');
        q.set(BRANCHES_PARAM, value);
    }
    // The basis only matters (and only shows) with a partial selection.
    const partial = selectedNames.length > 0 && selectedNames.length < allNames.length;
    if (partial && compare && compare !== DEFAULT_COMPARE) q.set(COMPARE_PARAM, compare);
    if (partial && compare === 'chosen' && chosen.length) q.set(CHOSEN_PARAM, chosen.join(','));
    const s = q.toString();
    return s ? `?${s}` : '';
};
